<template>
    <v-dialog v-model="dialog" width="400" persistent>
        <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="d-inline">
                <slot></slot>
            </div>
        </template>
        <v-card>
            <v-card-title class="flex-nowrap align-start">
                Edit File Quota
                <v-spacer></v-spacer>
                <v-btn @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-subtitle class="text-capitalize">{{ instance }} / {{ fileTypeLabel(type) }}</v-card-subtitle>
            <v-card-text class="mt-4">
                <v-form v-model="valid" ref="form">
                    <v-text-field
                        v-model="quotaValue"
                        v-mask="integerMask"
                        :rules="[rules.required, rules.positiveInteger, rules.maxQuotaLimit]"
                        outlined
                        dense
                        label="File quota"
                        min="1">
                        <template v-slot:append-outer>
                            <span class="mt-1">GB</span>
                        </template>
                    </v-text-field>
                </v-form>
                <v-alert type="error" text v-if="error">{{ error }}</v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn color="primary" text v-if="!success" :loading="sending" :disabled="!valid" @click="saveQuota()">Save</v-btn>
                <v-btn color="success" v-if="success"><v-icon>mdi-check</v-icon></v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { fetchTask } from '@/apis'
import fileQuotas from '@/mixins/fileQuotas'

const integerMask = createNumberMask({
    prefix: '',
    allowDecimal: false
})

export default {
    name: 'FileQuotaEditDialog',
    components: {},
    mixins: [fileQuotas],
    props: {
        quota: { type: Number },
        instance: { type: String },
        type: { type: String },
        maxQuota: { type: Number }
    },
    data() {
        return {
            dialog: false,
            integerMask,
            rules: {
                required: value => !!value || 'This field is required',
                positiveInteger: value => {
                    const num = parseInt(value.replace(/,/g, ''))
                    return (!isNaN(num) && num > 0) || 'Value must be a positive integer greater than 0'
                },
                maxQuotaLimit: value => {
                    if (!this.maxQuota) return true
                    const num = parseInt(value.replace(/,/g, ''))
                    const maxQuotaGB = Math.round(this.maxQuota / (1000 * 1000 * 1000))
                    return (!isNaN(num) && num <= maxQuotaGB) || `Value cannot exceed ${maxQuotaGB} GB`
                }
            },
            quotaValue: '',
            sending: false,
            success: false,
            error: null,
            valid: false
        }
    },
    methods: {
        async saveQuota() {
            this.error = null
            this.sending = true

            try {
                const quotaInBytes = parseInt(this.quotaInNumber) * (1000 * 1000 * 1000)
                const url = `/spaces/${this.$route.params.sid}/quotas`

                const { data } = await this.$axios.patch(url, {
                    new_quota_bytes: {
                        [this.instance]: {
                            [this.type]: quotaInBytes
                        }
                    }
                })

                const taskResult = await fetchTask(data.tkid)
                if (taskResult instanceof Error) {
                    throw taskResult
                } else {
                    this.success = true
                    this.$emit('success')
                    setTimeout(() => {
                        this.dialog = false
                    }, 2000)
                }
            } catch (error) {
                this.error = error?.response?.data?.message || error.message || 'An error occurred while saving the file quota.'
            }

            this.sending = false
        }
    },
    computed: {
        quotaInNumber() {
            return parseFloat(this.quotaValue.replace(/,/g, ''))
        }
    },
    watch: {
        dialog: {
            handler(open) {
                if (open) {
                    // Initialize with current quota value in GB
                    this.quotaValue = Math.round(this.quota / (1000 * 1000 * 1000)).toString()
                    this.success = false
                    this.error = null
                }
            },
            immediate: true
        }
    }
}
</script>
